/**
 * Base API utility functions
 */

import { refreshAuthCookie } from "../config/firebase";

const API_BASE_URL = "/api";

function isTokenExpiryResponse(status, data) {
  return (
    status === 401 &&
    data &&
    typeof data === "object" &&
    (data.requiresAuth === true ||
      (data.error && String(data.error).toLowerCase().includes("expired")))
  );
}

/** DB pool idle / ECONNRESET paths return 503 from auth middleware or /auth/set-cookie */
function isTransientAuthServiceResponse(status, data) {
  return (
    status === 503 &&
    data &&
    typeof data === "object" &&
    data.temporary === true
  );
}

function errorMessageFromStatus(status, data) {
  if (data && typeof data === "object" && typeof data.error === "string") {
    return data.error;
  }
  if (status === 413) {
    return "This file is too large for the server. Try a smaller image or contact support.";
  }
  if (status === 401) return "Please sign in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "That resource was not found.";
  if (status === 429) return "Too many requests. Please wait a moment and try again.";
  if (status === 502 || status === 504) {
    return "The service is temporarily unavailable. Please try again shortly.";
  }
  if (status >= 500) {
    return "The server had a problem. Please try again in a moment.";
  }
  return "An error occurred";
}

async function readJsonBody(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Generic fetch wrapper with error handling.
 * On 401 (expired/invalid token), refreshes the auth cookie and retries the request once.
 */
export const apiFetch = async (endpoint, options = {}, retried = false) => {
  const body = options.body;
  const isFormData =
    typeof FormData !== "undefined" && body instanceof FormData;

  const fetchOptions = {
    credentials: "include",
    ...options,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);

    const data = await readJsonBody(response);

    if (response.ok) {
      if (data === null) {
        return {};
      }
      return data;
    }

    if (isTokenExpiryResponse(response.status, data) && !retried) {
      const refreshed = await refreshAuthCookie();
      if (refreshed.ok) {
        return apiFetch(endpoint, options, true);
      }
    }

    if (isTransientAuthServiceResponse(response.status, data) && !retried) {
      const refreshed = await refreshAuthCookie();
      if (refreshed.ok) {
        return apiFetch(endpoint, options, true);
      }
    }

    throw new Error(errorMessageFromStatus(response.status, data));
  } catch (error) {
    if (error?.name === "AbortError") {
      throw error;
    }
    console.error("API Error:", error);
    throw error;
  }
};

/**
 * GET request
 */
export const get = (endpoint) => {
  return apiFetch(endpoint, { method: "GET" });
};

/**
 * POST request
 */
export const post = (endpoint, body) => {
  return apiFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(body),
  });
};

/** POST multipart (e.g. photo upload). Do not set Content-Type; browser sets boundary. */
export const postForm = (endpoint, formData, extra = {}) => {
  const { signal, headers, ...rest } = extra;
  return apiFetch(endpoint, {
    method: "POST",
    body: formData,
    signal,
    headers,
    ...rest,
  });
};

/**
 * PUT request
 */
export const put = (endpoint, body) => {
  return apiFetch(endpoint, {
    method: "PUT",
    body: JSON.stringify(body),
  });
};

/**
 * PATCH request
 */
export const patch = (endpoint, body) => {
  return apiFetch(endpoint, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
};

/**
 * DELETE request
 */
export const del = (endpoint, body) => {
  return apiFetch(endpoint, {
    method: "DELETE",
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * Accept a show
 */
export const acceptShow = (showId) => {
  return patch(`/shows/${showId}/accept`);
};

/**
 * Decline a show
 */
export const declineShow = (showId) => {
  return patch(`/shows/${showId}/decline`);
};

/**
 * Respond to a group of shows (accept/reject selected)
 */
export const respondToShowGroup = (
  showIdsToAccept,
  showIdsToReject,
  recurringGroupId
) => {
  return patch("/shows/group/respond", {
    showIdsToAccept: Array.isArray(showIdsToAccept) ? showIdsToAccept : [],
    showIdsToReject: Array.isArray(showIdsToReject) ? showIdsToReject : [],
    recurringGroupId,
  });
};

/**
 * Get shows by recurring group ID
 */
export const getShowsByGroup = (groupId) => {
  return get(`/shows/group/${groupId}`);
};

/**
 * Cancel shows (artist only)
 */
export const cancelShows = (showIdsToCancel, recurringGroupId) => {
  return del("/shows/cancel", {
    showIdsToCancel: Array.isArray(showIdsToCancel)
      ? showIdsToCancel
      : [showIdsToCancel],
    recurringGroupId,
  });
};

/**
 * Search user by email
 */
export const searchUserByEmail = (email) => {
  return get(`/users/search?email=${encodeURIComponent(email)}`);
};

/**
 * Send artist member invite
 */
export const sendArtistMemberInvite = (artistId, email, message = null) => {
  return post("/invites/artist-member", {
    artistId,
    email,
    message,
  });
};

/**
 * Send bar admin invite
 */
export const sendBarAdminInvite = (barIds, recipientEmail, message = null) => {
  return post("/invites/batch/admin", {
    barIds,
    recipientEmail,
    message,
  });
};

/**
 * Update artist profile
 */
export const updateArtist = (artistId, data) => {
  return put("/artists/", {
    artistId,
    ...data,
  });
};

/**
 * Remove member from artist
 */
export const removeMember = (artistId, userId) => {
  return del(`/artists/${artistId}/users/${userId}`);
};

/**
 * Update member role in artist
 */
export const updateMemberRole = (artistId, userId, role) => {
  return put(`/artists/${artistId}/users/${userId}/role`, { role });
};

/**
 * Leave artist group
 */
export const leaveArtistGroup = (artistId, newOwnerId = null) => {
  return del(
    `/artists/${artistId}/leave`,
    newOwnerId ? { newOwnerId } : undefined
  );
};

/**
 * Delete artist group (owner only). Soft-deletes the artist, notifies bars and other members.
 */
export const deleteArtistGroup = (artistId) => {
  return del(`/artists/${artistId}`);
};

/**
 * Cancel invite
 */
export const cancelInvite = (inviteId) => {
  return del(`/invites/${inviteId}`);
};
